////////////////////////////////////////////
// DEEPLINK ACTIONS — LAUNCH A WALLET-APP ROUND TRIP FROM THE NATIVE BROWSER
////////////////////////////////////////////
//
// EACH ACTION PERSISTS ITS PENDING RECORD, THEN NAVIGATES TO THE WALLET'S
// UNIVERSAL LINK. THE PAGE ISN'T UNLOADED BY THE APP SWITCH — THE RETURN
// REDIRECT LOADS A FRESH DOCUMENT WHERE resume.ts FINISHES THE ACTION — SO
// CALLERS' PROMISES SIMPLY NEVER SETTLE HERE. A VISIBILITY WATCHER UNSTICKS
// THE UI IF THE USER SWITCHES BACK WITHOUT THE WALLET REDIRECTING.
import type { SolNet } from "@/config/network";
import * as log from "@/lib/log";
import { b64ToB58, encryptPayload, makeKeypair, utf8ToB58 } from "./crypto";
import { connectUrl, signUrl } from "./protocol";
import {
  peekHandoff, peekPending, savePending, type DlProvider, type DlSession,
} from "./store";

////////////////////////////////////////////
// CONNECT — HOP 1: ESTABLISH THE ENCRYPTED SESSION
////////////////////////////////////////////
export function dlConnect(provider: DlProvider, net: SolNet, opts: { then?: "signin"; onCancel?: () => void } = {}) {

  const kp = makeKeypair();
  const at = Date.now();
  savePending({
    kind: "connect", provider, net,
    dappSecretKey: kp.secretKey, dappPublicKey: kp.publicKey,
    then: opts.then, at,
  });
  log.info("wallet", "deeplink connect → wallet app", { provider, net });
  armReturnWatcher(at, opts.onCancel);
  location.assign(connectUrl(provider, net, kp.publicKey));
}

////////////////////////////////////////////
// SIGN MESSAGE — THE SIGN-IN CHALLENGE (wallet + nonce RESUME THE VERIFY)
////////////////////////////////////////////
export function dlSignMessage(session: DlSession, message: string, ctx: { nonce: string; onCancel?: () => void }) {

  const enc = encryptPayload(
    { message: utf8ToB58(message), session: session.session, display: "utf8" },
    session.sharedSecret
  );
  const at = Date.now();
  savePending({ kind: "signin", provider: session.provider, wallet: session.pubkey, nonce: ctx.nonce, at });
  log.info("wallet", "deeplink sign-in → wallet app", { provider: session.provider });
  armReturnWatcher(at, ctx.onCancel);
  location.assign(signUrl(session.provider, "signMessage", "signin", session.dappPublicKey, enc.nonce, enc.data));
}

////////////////////////////////////////////
// SIGN TRANSACTION — THE SERVER-BUILT TX; SUBMIT CONTEXT RIDES THE PENDING
////////////////////////////////////////////
export function dlSignTransaction(session: DlSession, p: {
  txBase64: string;
  net: string;
  action: string;
  ok: string;
  successPath?: string;
  onCancel?: () => void;
}) {

  const enc = encryptPayload(
    { transaction: b64ToB58(p.txBase64), session: session.session },
    session.sharedSecret
  );
  const at = Date.now();
  savePending({
    kind: "tx", provider: session.provider, net: p.net,
    action: p.action, ok: p.ok, successPath: p.successPath, at,
  });
  log.info(p.action, "deeplink sign → wallet app", { provider: session.provider, net: p.net });
  armReturnWatcher(at, p.onCancel);
  location.assign(signUrl(session.provider, "signTransaction", "tx", session.dappPublicKey, enc.nonce, enc.data));
}

////////////////////////////////////////////
// RETURN WATCHER — THE ORIGINATING TAB COMES BACK INTO VIEW
////////////////////////////////////////////
//
// ONE MODULE-LEVEL LISTENER, RE-ARMED PER FLOW (REPEATED FLOWS MUST NOT
// STACK LISTENERS). WHEN THE TAB BECOMES VISIBLE: IF OUR PENDING IS STILL
// ARMED THE USER PEEKED BACK MID-FLOW — UNSTICK THE UI BUT KEEP WATCHING (A
// LATER APPROVAL STILL RESUMES THROUGH A FOREIGN TAB). IF IT WAS CONSUMED,
// POLL FOR THE FOREIGN TAB'S HANDOFF — ITS SUBMIT/VERIFY CAN TAKE SECONDS —
// THEN FOLLOW ITS REDIRECT OR RELOAD IN PLACE (THE FRESH LOAD CONSUMES THE
// HANDOFF TOAST). NO HANDOFF WITHIN THE WINDOW → DISARM SILENTLY; A BLIND
// RELOAD LONG AFTER AN ABANDONED FLOW WOULD YANK THE PAGE FROM THE USER.
////////////////////////////////////////////

const HANDOFF_POLL_MS = 800;
const HANDOFF_WAIT_MS = 30_000;

interface ArmedWatch {
  at: number;
  onCancel?: () => void;
}

let armed: ArmedWatch | null = null;
let listening = false;

function armReturnWatcher(at: number, onCancel?: () => void) {

  armed = { at, onCancel };
  if (listening)
    return;
  listening = true;
  document.addEventListener("visibilitychange", onReturnVisible);
}

function onReturnVisible() {

  if (!armed || document.visibilityState !== "visible")
    return;

  const watch = armed;
  let waited = 0;
  const tick = () => {

    // RE-HIDDEN, RE-ARMED BY A NEWER FLOW, OR DISARMED — THIS PASS IS OVER
    if (armed !== watch || document.visibilityState !== "visible")
      return;

    const p = peekPending();
    if (p) {

      // OUR FLOW STILL ARMED — THE USER BAILED FOR NOW; A NEWER RECORD MEANS
      // A FRESH FLOW OWNS THE WATCH
      if (p.at === watch.at)
        watch.onCancel?.();
      else
        armed = null;
      return;
    }

    const h = peekHandoff();
    if (h) {

      armed = null;
      if (h.redirect)
        location.assign(h.redirect);
      else
        location.reload();
      return;
    }

    waited += HANDOFF_POLL_MS;
    if (waited >= HANDOFF_WAIT_MS) {

      armed = null;
      return;
    }
    setTimeout(tick, HANDOFF_POLL_MS);
  };
  setTimeout(tick, 2000);
}
